import { useEffect, useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import { apiService } from '@/service/api'
import { useTranslation } from '@/i18n'

interface SetPreviewOverlayProps {
  docId: string
  docName: string
  onClose: () => void
}

export default function SetPreviewOverlay({ docId, docName, onClose }: SetPreviewOverlayProps) {
  const { t } = useTranslation()
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [fileType, setFileType] = useState<'image' | 'pdf'>('pdf')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let revoked = false
    apiService.downloadDocument(docId).then((blob) => {
      if (revoked) return
      setBlobUrl(URL.createObjectURL(blob))
      setFileType(blob.type.startsWith('image/') ? 'image' : 'pdf')
      setLoading(false)
    }).catch(() => {
      if (!revoked) setLoading(false)
    })
    return () => {
      revoked = true
      setBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null })
    }
  }, [docId])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[9999999] overflow-hidden"
      style={{ backgroundColor: 'rgba(34, 34, 34, 0.5)' }}
      onClick={onClose}
    >
      {/* Floating filename card (top-left) */}
      <div className="absolute top-3 left-3 z-20 bg-white dark:bg-dark-card rounded-[10px] shadow-xs border border-border px-3.5 py-2.5 h-[52px] w-[420px] flex items-center">
        <span className="text-base font-medium text-foreground truncate">
          {docName}
        </span>
      </div>

      {/* Floating close button (top-right) */}
      <button
        type="button"
        className="absolute top-3 right-3 z-20 bg-white dark:bg-dark-card rounded-[10px] shadow-xs border border-border h-[52px] w-[52px] flex items-center justify-center hover:bg-muted transition-colors"
        onClick={(e) => { e.stopPropagation(); onClose() }}
      >
        <X className="size-6 text-foreground" />
      </button>

      {/* Document preview — white card matching Figma */}
      <div
        className="absolute top-[100px] left-1/2 -translate-x-1/2 w-[1000px] h-[700px] bg-white dark:bg-dark-card dark:border dark:border-dark-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : blobUrl ? (
          fileType === 'image' ? (
            <img
              src={blobUrl}
              alt={docName}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              draggable={false}
            />
          ) : (
            <iframe
              src={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              title={docName}
              className="w-full h-full border-0"
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-sm text-muted-foreground">{t('graph_editor:pidSet.previewNotAvailable', { defaultValue: 'Preview not available' })}</span>
          </div>
        )}
      </div>
    </div>
  )
}
